import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Shield, ChevronRight } from 'lucide-react';
import ContactPopup from './ContactPopup';
import MegaMenu from './MegaMenu';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const location = useLocation();

  // Listen for custom event from mega menu to open contact popup
  useEffect(() => {
    const handleOpenPopup = () => setShowPopup(true);
    window.addEventListener('openContactPopup', handleOpenPopup);
    return () => window.removeEventListener('openContactPopup', handleOpenPopup);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    {
      name: 'Services',
      submenu: [
        { name: 'Trademark Registration', path: '/services/trademark-registration' },
        { name: 'Copyright Registration', path: '/services/copyright-registration' },
        { name: 'Company Formation', path: '/services/company-formation' },
        { name: 'Trademark Search', path: '/services/trademark-search' },
        { name: 'Amazon Brand Registry', path: '/services/amazon-brand-registry' },
      ],
    },
    { name: 'Contact Us', path: '/contact' },
  ];

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <nav className="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img 
                src="/assets/img/brand/logo-dark.png" 
                alt="Orbit Marks" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {/* Phone Number */}
              <a 
                href="tel:+18333729022" 
                className="flex items-center text-indigo-600 font-semibold hover:text-indigo-700 transition-colors mr-6"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                +1 (833) 372 9022
              </a>
              {navLinks.map((link, index) => (
                <div key={index} className="relative">
                  {link.submenu ? (
                    <div
                      onMouseEnter={() => setShowMegaMenu(true)}
                      onMouseLeave={() => setShowMegaMenu(false)}
                      className="relative"
                    >
                      <button
                        className="text-gray-700 hover:text-indigo-600 font-medium px-4 py-2 rounded-lg hover:bg-indigo-50 transition-all"
                        data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        {link.name}
                      </button>
                      {/* Invisible bridge to prevent menu from closing */}
                      <div className="absolute left-0 right-0 h-2 top-full" />
                      <MegaMenu 
                        isOpen={showMegaMenu} 
                        onClose={() => setShowMegaMenu(false)} 
                      />
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className={`text-gray-700 hover:text-indigo-600 font-medium px-4 py-2 rounded-lg hover:bg-indigo-50 transition-all ${
                        isActivePath(link.path) ? 'text-indigo-600 bg-indigo-50' : ''
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Right side buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <button
                onClick={() => setShowPopup(true)}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-full hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all"
                data-testid="get-started-button"
              >
                Get Started
              </button>
              <button
                onClick={() => {
                  try {
                    // Open Zendesk chat widget using Classic Web Widget API
                    if (typeof window !== 'undefined' && window.zE) {
                      window.zE('webWidget', 'open');
                    } else {
                      console.log('Zendesk widget not loaded yet');
                      // Fallback: try to click the launcher button
                      setTimeout(() => {
                        const launcher = document.querySelector('#launcher');
                        if (launcher) {
                          launcher.click();
                        }
                      }, 100);
                    }
                  } catch (error) {
                    console.error('Error opening chat:', error);
                  }
                }}
                className="px-6 py-2.5 border-2 border-indigo-600 text-indigo-600 font-semibold rounded-full hover:bg-indigo-50 transition-all js-chat"
                data-testid="chat-now-button"
              >
                Chat Now
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-gray-700"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="lg:hidden py-4 border-t" data-testid="mobile-menu">
              {navLinks.map((link, index) => (
                <div key={index}>
                  {link.submenu ? (
                    <MobileServicesMenu onClose={() => setIsOpen(false)} />
                  ) : (
                    <Link
                      to={link.path}
                      className={`block px-4 py-3 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition-all ${
                        isActivePath(link.path) ? 'text-indigo-600 bg-indigo-50' : ''
                      }`}
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="px-4 mt-4 space-y-3">
                <button
                  onClick={() => {
                    setShowPopup(true);
                    setIsOpen(false);
                  }}
                  className="w-full px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-full hover:from-indigo-700 hover:to-purple-700"
                  data-testid="mobile-get-started-button"
                >
                  Get Started
                </button>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    try {
                      // Open Zendesk chat widget using Classic Web Widget API
                      if (typeof window !== 'undefined' && window.zE) {
                        window.zE('webWidget', 'open');
                      } else {
                        console.log('Zendesk widget not loaded yet');
                        // Fallback: try to click the launcher button
                        setTimeout(() => {
                          const launcher = document.querySelector('#launcher');
                          if (launcher) {
                            launcher.click();
                          }
                        }, 100);
                      }
                    } catch (error) {
                      console.error('Error opening chat:', error);
                    }
                  }}
                  className="w-full px-6 py-2.5 border-2 border-indigo-600 text-indigo-600 font-semibold rounded-full hover:bg-indigo-50 js-chat"
                  data-testid="mobile-chat-now-button"
                >
                  Chat Now
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} source="popup" />}
    </>
  );
};

// Mobile Services Menu Component
const MobileServicesMenu = ({ onClose }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const menuStructure = [
    {
      title: 'Trademark Services',
      items: [
        { name: 'Register Your Trademark', path: '/services/register-trademark' },
        { name: 'Clearance Search Services', path: '/services/clearance-search' },
        { name: 'Trademark Ownership Transfers', path: '/services/trademark-transfers' },
        { name: 'Submission of Statements of Use', path: '/services/statements-of-use' },
        { name: 'Support and Response for Trademark Office Actions', path: '/services/trademark-office-actions' },
        { name: 'Active Monitoring for Trademark infringement', path: '/services/trademark-monitoring' },
        { name: 'File TTAB Petition', path: '/services/ttab-petition' },
      ],
    },
    {
      title: 'Company Formation Services',
      items: [
        { name: 'Limited Liability Company', path: '/services/llc-formation' },
        { name: 'C Corporation', path: '/services/c-corporation' },
        { name: 'S Corporation', path: '/services/s-corporation' },
        { name: 'Non-Profit', path: '/services/non-profit' },
      ],
    },
    {
      title: 'Copyright Services',
      items: [
        { name: 'Register Your Copyright', path: '/services/register-copyright' },
        { name: 'Assistance for Your Copyright Enforcement', path: '/services/copyright-enforcement' },
        { name: 'Copyright Transfers and Licensing Support', path: '/services/copyright-transfers' },
        { name: 'Legal Representation for Your Copyright', path: '/services/copyright-legal-representation' },
        { name: 'Management of Your Copyright Portfolio', path: '/services/copyright-portfolio' },
        { name: 'Compliance with DMCA Act', path: '/services/dmca-compliance' },
      ],
    },
    {
      title: 'Patent Services',
      items: [
        { name: 'Search for Existing Patent', path: '/services/patent-search' },
        { name: 'Support and Response for Patent Office Actions', path: '/services/patent-office-actions' },
        { name: 'Application Submission for Patent', path: '/services/patent-application' },
        { name: 'Legal Representation for Patent Protection', path: '/services/patent-legal-representation' },
      ],
    },
    {
      title: 'Legal Services',
      items: [
        { name: 'Business Immigration Law', path: '/services/business-immigration' },
        { name: 'Due Diligence Process', path: '/services/due-diligence' },
        { name: 'IT & Commercial Law', path: '/services/it-commercial-law' },
        { name: 'Legal Services on Partnerships & LLPs', path: '/services/partnerships-llp' },
        { name: 'Data Protection & Privacy Law', path: '/services/data-protection' },
      ],
    },
    {
      title: 'Additional Corporate Services',
      items: [
        { name: 'Amazon Brand Registry in the US', path: '/services/amazon-brand-registry' },
        { name: 'Bookkeeping Service', path: '/services/bookkeeping' },
        { name: 'Business Formation Services', path: '/services/business-formation' },
        { name: 'Certificate of Good Standing', path: '/services/certificate-good-standing' },
        { name: 'Company Dissolution', path: '/services/company-dissolution' },
        { name: 'Company Name Change', path: '/services/company-name-change' },
        { name: 'Company Renewal Services', path: '/services/company-renewal' },
        { name: 'Issue Of Shares', path: '/services/issue-of-shares' },
        { name: 'Nominee Director & Secretarial Services', path: '/services/nominee-director' },
        { name: 'Tax Advisory & Filing', path: '/services/tax-advisory' },
        { name: 'Transfer of Shares', path: '/services/transfer-of-shares' },
        { name: 'CFO Services', path: '/services/cfo-services' },
      ],
    },
  ];

  return (
    <div className="px-4 py-2">
      <div className="text-gray-700 font-semibold mb-2">Services</div>
      {menuStructure.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-2">
          <button
            onClick={() => setExpandedCategory(expandedCategory === categoryIndex ? null : categoryIndex)}
            className="w-full flex items-center justify-between px-3 py-2 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-all"
          >
            <span className="text-sm font-medium text-gray-700">{category.title}</span>
            <ChevronRight 
              className={`text-gray-500 transition-transform ${expandedCategory === categoryIndex ? 'rotate-90' : ''}`}
              size={16}
            />
          </button>
          {expandedCategory === categoryIndex && (
            <div className="mt-1 ml-2 space-y-1">
              {category.items.map((item, itemIndex) => (
                <Link
                  key={itemIndex}
                  to={item.path}
                  onClick={onClose}
                  className="block px-4 py-2 text-sm text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition-all"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default Navigation;